import { cva, VariantProps } from "class-variance-authority";
import { CheckCircle, LoaderCircle, XCircle } from "lucide-react";
import { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";
import { LangKey, useTranslation } from "@vixi/localization";
import { cn } from "@/lib";

// Constants
const TOAST_DURATION = 4000;
const TOAST_LOADING: ToastContentProps = {
  translate: true,
  title: "LoadingMessage.Loading",
  description: "LoadingMessage.PleaseWaitWhileWeProcessYourRequest",
  icon: <LoaderCircle className="animate-spin" />,
};
const TOAST_SUCCESS: ToastContentProps = {
  translate: true,
  title: "SuccessMessage.Success",
  description: "SuccessMessage.OperationCompleted",
  icon: (
    <div className="animate-spin">
      <CheckCircle />
    </div>
  ),
};
const TOAST_ERROR: ToastContentProps = {
  translate: true,
  title: "ErrorMessage.Error",
  description: "ErrorMessage.ThereWasAnError",
  icon: <XCircle />,
};

// Styling
export const toastVariants = cva(
  "rounded-md bg-paper min-w-[270px] border-2 ring-1 ring-black/5 w-full md:max-w-[400px] p-4",
  {
    variants: {
      colors: {
        default: "border-primary",
        loading: "border-foreground",
        success: "border-success",
        error: "border-destructive",
        warning: "border-amber-400",
      },
    },
    defaultVariants: {
      colors: "default",
    },
  },
);

const colorVariants = cva("", {
  variants: {
    color: {
      default: "text-primary",
      success: "text-success",
      error: "text-destructive",
      warning: "text-amber-400",
      loading: "text-foreground",
    } satisfies Record<keyof VariantProps<typeof toastVariants>["colors"], string>,
  },
  defaultVariants: {
    color: "default",
  },
});

// Types
type ToastContentProps = {
  icon?: ReactNode;
  translate?: boolean;
  title: ReactNode | LangKey;
  description: ReactNode | LangKey;
  button?: ReactNode | LangKey;
};

export type DefinedToastProps = VariantProps<typeof toastVariants> & ToastContentProps;
export type PromiseToastProps = {
  promise: Promise<any>;
  successContent?: ToastContentProps;
  errorContent?: ToastContentProps;
  loadingContent?: ToastContentProps;
};

export type AnyToastProps = DefinedToastProps | PromiseToastProps;
export type ToastPropsWithId = DefinedToastProps & { id?: string | number };

/** I recommend abstracting the toast function
 *  so that you can call it without having to use toast.custom everytime. */
export function toast(toast: DefinedToastProps): string | number {
  return sonnerToast.custom((id) => <Toast {...toast} id={id} />);
}

export function promiseToast(toast: PromiseToastProps) {
  sonnerToast.promise(toast.promise, {
    duration: TOAST_DURATION,
    unstyled: true,
    icon: null,
    loading: <Toast {...{ ...TOAST_LOADING, ...toast.loadingContent }} colors="loading" />,
    success: <Toast {...{ ...TOAST_SUCCESS, ...toast.successContent }} colors="success" />,
    error: <Toast {...{ ...TOAST_ERROR, ...toast.errorContent }} colors="error" />,
  });
}

function ToastContent(props: ToastContentProps) {
  const { t } = useTranslation();
  const { translate, title, description, button, icon } = props;

  return (
    <div className="flex items-center gap-4">
      {icon && <div className="icon">{icon}</div>}
      <div className="content">
        <h4 className={"text-lg text-current"}>{translate ? t(title as LangKey) : title}</h4>
        <p className="text-text-secondary text-sm">
          {translate ? t(description as LangKey) : description}
        </p>
      </div>
      {button && <div className="action">{button}</div>}
    </div>
  );
}

/** A fully custom toast that still maintains the animations and interactions. */
function Toast(props: ToastPropsWithId) {
  const { colors = "default", translate, title, description, button, icon } = props;

  return (
    <div className={cn(toastVariants({ colors }), colorVariants({ color: colors }))}>
      <ToastContent
        translate={translate}
        title={title}
        description={description}
        button={button}
        icon={
          icon && (
            <div className={cn("me-4 size-5 *:size-full", colorVariants({ color: colors }))}>
              {icon}
            </div>
          )
        }
      />
    </div>
  );
}
